<script>
  import { createEventDispatcher } from "svelte";
  import { Link } from "framework7-svelte";

  export let positions = [];
  export let filteredPositions = [];
  export let posCounts = { all: 0, open: 0, profit: 0, loss: 0 };
  export let openPositionsInFiltered = [];
  export let posSearchQuery = "";
  export let posStatusFilter = "OPEN_ONLY";
  export let posSortCol = "pnl";
  export let posSortDir = "asc";
  export let flashingRowIds = new Set();
  export let terminalEnv = "LIVE";
  export let isSplit = false;

  const dispatch = createEventDispatcher();

  function toggleSort(col) {
    dispatch("sort", col);
  }

  function handleSquareOff(pos) {
    dispatch("squareOffPosition", pos);
  }

  function handleBulkSquareOff() {
    dispatch("bulkSquareOff");
  }

  function handleScripSquareOff(symbol) {
    dispatch("squareOffScrip", symbol);
  }

  function handleClientClick(client, user) {
    dispatch("openClient", { client, user });
  }
</script>

<div class="card data-table terminal-grid-card">
  <div class="card-header display-flex justify-content-space-between align-items-center">
    <div style="display:flex; align-items:center; gap:8px;">
      <span style="font-weight:700;">
        Client Net Positions
      </span>
      <span class="grid-badge-total">{filteredPositions.length} position{filteredPositions.length === 1 ? "" : "s"}</span>
    </div>
    {#if !isSplit}
      <span style="font-size: 12px; color: #64748b;">Click UCC Tag to inspect • Click headers to sort</span>
    {/if}
  </div>

  <!-- Quick Filter & Bulk Actions Toolbar -->
  <div class="grid-toolbar">
    <div class="toolbar-left">
      <!-- Text Search -->
      <div class="search-input-wrapper">
        <span class="search-icon">🔍</span>
        <input
          type="text"
          class="grid-search-input"
          placeholder={isSplit ? "Search positions..." : "Search Symbol, Client UCC, Name..."}
          bind:value={posSearchQuery}
        />
        {#if posSearchQuery}
          <button class="clear-search-btn" on:click={() => (posSearchQuery = "")}>✕</button>
        {/if}
      </div>

      <!-- Segmented Status Pills -->
      <div class="filter-pill-group">
        <button
          class="filter-pill {posStatusFilter === 'ALL' ? 'active' : ''}"
          on:click={() => (posStatusFilter = "ALL")}
        >
          ALL <span class="pill-count">({posCounts.all})</span>
        </button>
        <button
          class="filter-pill {posStatusFilter === 'OPEN_ONLY' ? 'active' : ''}"
          on:click={() => (posStatusFilter = "OPEN_ONLY")}
        >
          OPEN <span class="pill-count">({posCounts.open})</span>
        </button>
        <button
          class="filter-pill {posStatusFilter === 'PROFIT' ? 'active' : ''}"
          on:click={() => (posStatusFilter = "PROFIT")}
        >
          PROFIT <span class="pill-count">({posCounts.profit})</span>
        </button>
        <button
          class="filter-pill {posStatusFilter === 'LOSS' ? 'active' : ''}"
          on:click={() => (posStatusFilter = "LOSS")}
        >
          LOSS <span class="pill-count">({posCounts.loss})</span>
        </button>
      </div>
    </div>

    <div class="toolbar-right">
      <!-- Bulk Square Off Filtered Button -->
      <button
        class="bulk-action-btn bulk-btn-sqoff"
        disabled={openPositionsInFiltered.length === 0}
        on:click={handleBulkSquareOff}
        title="Square off all open positions matching current filter"
      >
        ⚡ Square Off ({openPositionsInFiltered.length})
      </button>
    </div>
  </div>

  <div class="card-content" style="padding:0; overflow-x: auto; max-height: calc(100vh - 280px); overflow-y: auto;">
    <table class="terminal-compact-table">
      <thead>
        <tr>
          <th class="label-cell sortable-th" on:click={() => toggleSort("client")}>
            Client UCC <span class="sort-icon">{posSortCol === "client" ? (posSortDir === "asc" ? "▲" : "▼") : "↕"}</span>
          </th>
          <th class="label-cell sortable-th" on:click={() => toggleSort("symbol")}>
            Symbol <span class="sort-icon">{posSortCol === "symbol" ? (posSortDir === "asc" ? "▲" : "▼") : "↕"}</span>
          </th>
          <th class="numeric-cell sortable-th" on:click={() => toggleSort("netQty")}>
            Net Qty <span class="sort-icon">{posSortCol === "netQty" ? (posSortDir === "asc" ? "▲" : "▼") : "↕"}</span>
          </th>
          <th class="numeric-cell sortable-th" on:click={() => toggleSort("buyAvg")}>
            Fill Avg <span class="sort-icon">{posSortCol === "buyAvg" ? (posSortDir === "asc" ? "▲" : "▼") : "↕"}</span>
          </th>
          <th class="numeric-cell sortable-th" on:click={() => toggleSort("ltp")}>
            LTP <span class="sort-icon">{posSortCol === "ltp" ? (posSortDir === "asc" ? "▲" : "▼") : "↕"}</span>
          </th>
          <th class="numeric-cell sortable-th" on:click={() => toggleSort("pnl")}>
            Client P&amp;L <span class="sort-icon">{posSortCol === "pnl" ? (posSortDir === "asc" ? "▲" : "▼") : "↕"}</span>
          </th>
          <th class="label-cell" style="min-width: 120px;">Actions</th>
        </tr>
      </thead>
      <tbody>
        {#if filteredPositions.length === 0}
          <tr>
            <td colspan="7" style="text-align:center; padding: 24px; color: #94a3b8;">
              {positions.length === 0
                ? "No open positions across clients"
                : "No positions match the current filter criteria"}
            </td>
          </tr>
        {:else}
          {#each filteredPositions as pos (pos.client + '_' + pos.symbol)}
            <tr class="{flashingRowIds.has(pos.client + '_' + pos.symbol) ? (pos.pnl >= 0 ? 'flash-update-up' : 'flash-update-down') : ''}">
              <td class="label-cell">
                <button
                  type="button"
                  class="client-pill-btn"
                  on:click={() => handleClientClick(pos.client, pos.user)}
                  title="Click to inspect client controls"
                >
                  <span class="client-tag-badge">{pos.client}</span>
                  {#if pos.user}
                    <span class="user-subname">{pos.user.fullName}</span>
                  {:else}
                    <span class="user-subname unlinked">Unlinked</span>
                  {/if}
                </button>
              </td>
              <td class="label-cell font-bold">
                <button
                  type="button"
                  class="symbol-chart-btn"
                  on:click={() => dispatch("openChart", { symbol: pos.symbol, ucc: pos.client })}
                  title="View live signal chart"
                >
                  {pos.symbol} <span style="font-size:10px; color:#3b82f6;">📈</span>
                </button>
              </td>
              <td
                class="numeric-cell font-mono-tabular"
                style="font-weight: 700; color: {pos.netQty > 0 ? '#16a34a' : pos.netQty < 0 ? '#dc2626' : '#64748b'};"
              >
                {pos.netQty > 0 ? `+${pos.netQty}` : pos.netQty}
              </td>
              <td class="numeric-cell font-mono-tabular">₹{Number(pos.buyAvg || 0).toFixed(2)}</td>
              <td class="numeric-cell font-mono-tabular">₹{Number(pos.ltp || 0).toFixed(2)}</td>
              <td
                class="numeric-cell font-mono-tabular"
                style="color: {pos.pnl >= 0 ? '#16a34a' : '#dc2626'}; font-weight: bold;"
              >
                {pos.pnl >= 0 ? "+" : ""}₹{Number(pos.pnl || 0).toFixed(2)}
              </td>
              <td class="label-cell">
                <div style="display:flex; align-items:center; gap:4px;">
                  {#if pos.netQty !== 0}
                    <button
                      type="button"
                      class="row-action-btn btn-sqoff"
                      on:click={() => handleSquareOff(pos)}
                      title="Square off this individual position"
                    >
                      ⚡ Sq-Off
                    </button>
                    <button
                      type="button"
                      class="row-action-btn btn-sqoff"
                      style="background:#dc2626; color:#ffffff; border-color:#b91c1c;"
                      on:click={() => handleScripSquareOff(pos.symbol)}
                      title="Emergency square off {pos.symbol} across ALL clients (strategyapisqr4)"
                    >
                      ⚡ Scrip All
                    </button>
                  {/if}
                  <Link
                    href={`/user-trading-controls/?ucc=${encodeURIComponent(pos.client)}`}
                    class="button button-small button-outline button-round"
                    style="display: inline-flex; align-items: center; gap: 2px; padding: 0 6px; font-size:10px; height:20px; line-height:20px;"
                  >
                    &rarr;
                  </Link>
                </div>
              </td>
            </tr>
          {/each}
        {/if}
      </tbody>
    </table>
  </div>
</div>

<style>
  .symbol-chart-btn {
    background: transparent;
    border: none;
    color: #e2e8f0;
    font-weight: 700;
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: background 0.15s;
  }
  .symbol-chart-btn:hover {
    background: rgba(59, 130, 246, 0.2);
    color: #60a5fa;
  }
</style>
